import { AxiosError } from 'axios';

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Address } from 'viem';

import { get, post } from '@helpers/common/axios.helper';
import { parseAxiosError } from '@helpers/common/error.helpers';

import showToast from '@components/common/Toast';

import {
  API_BOND_ACTION_HISTORY,
  API_BOND_DISCLAIMER_ACCEPT,
  API_BOND_DISCLAIMER_ACCEPTED,
  API_BOND_INFO,
  API_BOND_PERFORMANCE_METRICS,
  API_BOND_REFERRAL_ME,
  API_BOND_SIGN_MINT_AND_APPLY,
  API_BOND_SIGN_REDEEM,
  API_BOND_STATS_SNAPSHOTS,
  API_BOND_TOTAL_REWARDS_DISTRIBUTED,
  API_COMMON_PERSONAL_KPI,
} from '@constants/bonds/api.constants';
import { BOND_DISCLAIMER_ACCEPTABLE_VERSIONS } from '@constants/bonds/disclaimer.constants';
import { ToastType } from '@constants/common/toast.constants';

import {
  AcceptDisclaimerBody,
  BondDisclaimerVersion,
  BondsActionHistoryDto,
  BondsChartPointDto,
  BondsInfoDto,
  BondsPerformanceMetricsDto,
  BondsTotalRewardsDistributedDto,
  PersonalKpiDto,
  ReferralMeDto,
  SignMintAndApplyDto,
  SignRedeemDto,
} from '@dto/bonds/bonds.dto';

const getBondsInfo = (): Promise<BondsInfoDto> =>
  get<BondsInfoDto>(API_BOND_INFO);

export const useBondsInfoRequest = () =>
  useQuery<BondsInfoDto>({
    queryKey: [API_BOND_INFO],
    queryFn: () => getBondsInfo(),
    retry: false,
    refetchInterval: 60000,
  });

const signRedeem = (params: {
  signature: string;
  data: {
    account: Address;
    nftsAmount: number;
    timestamp: number;
  };
}): Promise<SignRedeemDto> => post<SignRedeemDto>(API_BOND_SIGN_REDEEM, params);

export const useSignRedeem = () =>
  useMutation({
    mutationFn: signRedeem,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
  });

const getBondsTotalRewardsDistributed =
  (): Promise<BondsTotalRewardsDistributedDto> =>
    get<BondsTotalRewardsDistributedDto>(API_BOND_TOTAL_REWARDS_DISTRIBUTED);

export const useBondsTotalRewardsDistributed = () =>
  useQuery<BondsTotalRewardsDistributedDto>({
    queryKey: [API_BOND_TOTAL_REWARDS_DISTRIBUTED],
    queryFn: () => getBondsTotalRewardsDistributed(),
    retry: false,
    refetchInterval: 60000,
  });

const getBondsStatsSnapshots = (): Promise<BondsChartPointDto[]> =>
  get<BondsChartPointDto[]>(API_BOND_STATS_SNAPSHOTS);

export const useBondsStatsSnapshots = () =>
  useQuery<BondsChartPointDto[]>({
    queryKey: [API_BOND_STATS_SNAPSHOTS],
    queryFn: getBondsStatsSnapshots,
    select: (data) => [...data].reverse(),
    retry: false,
    refetchInterval: 60000,
  });

const getBondsActionHistory = (
  account?: Address,
): Promise<BondsActionHistoryDto[]> =>
  get<BondsActionHistoryDto[]>(API_BOND_ACTION_HISTORY, { account });

export const useBondsActionHistory = (account?: Address) =>
  useQuery<BondsActionHistoryDto[]>({
    queryKey: [API_BOND_ACTION_HISTORY, account],
    queryFn: () => getBondsActionHistory(account),
    retry: false,
    refetchInterval: 60000,
    enabled: !!account,
  });

const getBondsPerformanceMetrics = (
  account?: Address,
): Promise<BondsPerformanceMetricsDto> =>
  get<BondsPerformanceMetricsDto>(API_BOND_PERFORMANCE_METRICS, { account });

export const useBondsPerformanceMetrics = (account?: Address) =>
  useQuery<BondsPerformanceMetricsDto>({
    queryKey: [API_BOND_PERFORMANCE_METRICS, account],
    queryFn: () => getBondsPerformanceMetrics(account),
    retry: false,
    refetchInterval: 60000,
    enabled: !!account,
  });

const signMintAndApply = (params: {
  signature: string;
  data: {
    account: Address;
    nftsAmount: number;
    timestamp: number;
    referralCode: string | null;
  };
}): Promise<SignMintAndApplyDto> =>
  post<SignMintAndApplyDto>(API_BOND_SIGN_MINT_AND_APPLY, params);

export const useSignMintAndApply = () =>
  useMutation({
    mutationFn: signMintAndApply,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
  });

const getReferralMe = (account?: Address): Promise<ReferralMeDto> =>
  get<ReferralMeDto>(API_BOND_REFERRAL_ME, { account });

export const useReferralMe = (account?: Address) =>
  useQuery<ReferralMeDto>({
    queryKey: [API_BOND_REFERRAL_ME, account],
    queryFn: () => getReferralMe(account),
    retry: false,
    refetchInterval: 60000,
    enabled: !!account,
  });

// VAULTS API

export const getPersonalKpi = (account?: string) =>
  get<PersonalKpiDto>(API_COMMON_PERSONAL_KPI, { account });

export const useGetPersonalKpi = (account?: string) =>
  useQuery<PersonalKpiDto>({
    queryKey: [API_COMMON_PERSONAL_KPI, account],
    queryFn: () => getPersonalKpi(account),
    retry: false,
    enabled: !!account,
  });

// DISCLAIMER

const getIsDisclaimerAccepted = (
  address: Address | undefined,
  disclaimerVersion: BondDisclaimerVersion,
): Promise<boolean> =>
  get<boolean>(API_BOND_DISCLAIMER_ACCEPTED, { address, disclaimerVersion });

export const useIsDisclaimerAccepted = (address?: Address) => {
  const queries = useQueries({
    queries: BOND_DISCLAIMER_ACCEPTABLE_VERSIONS.map((version) => ({
      queryKey: [API_BOND_DISCLAIMER_ACCEPTED, address, version],
      queryFn: () => getIsDisclaimerAccepted(address, version),
      enabled: !!address,
      retry: false,
      staleTime: Infinity,
    })),
  });

  const isAccepted = queries.some((q) => q.data === true);

  return {
    // If any version already returned true → user is in. Don't mask that behind
    // a "loading" flag while the other version-query lingers.
    data: isAccepted,
    isLoading: !isAccepted && queries.some((q) => q.isLoading),
  };
};

const acceptDisclaimer = (body: AcceptDisclaimerBody): Promise<void> =>
  post<void>(API_BOND_DISCLAIMER_ACCEPT, body);

export const useAcceptDisclaimer = () => {
  const queryClient = useQueryClient();

  // Refresh both v1 and v1_hash acceptance queries after the POST (and on a
  // 409 "already accepted") — the gate ORs them, so the modal closes when
  // either flips to true.
  const invalidateAcceptanceQueries = (address: Address) =>
    queryClient.invalidateQueries({
      queryKey: [API_BOND_DISCLAIMER_ACCEPTED, address],
    });

  return useMutation({
    mutationFn: acceptDisclaimer,
    onSettled: async (_data, _error, vars) => {
      await invalidateAcceptanceQueries(vars.address);
    },
  });
};
